// Imports (CHANGE-070): the founders' local files reach the records here. scripts/crm_bundle.py makes one bundle.json on
// the Mac, a founder uploads it, reads the dry run, then applies that exact file. Apply is bound to the dry run's sha256
// and refused when the records moved since, so what gets written is what was read. Nothing here sends anything.
import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../lib'
import { Busy, Button, Copy, Err, Fresh, Page, Section, Sources, Table, since, errorText, num, useKit, when, type Column } from '../kit'

type Sec = { matched: number; created: number; changed: number; kept: number; skipped: number; conflicts: number }
type Plan = { sections: Record<string, Sec>; stage_changes: number }
type FileRow = { file: string; read?: number; used?: number; skipped?: number; conflicts?: number; missing?: boolean; error?: string }
type ConflictRow = { section: string; key: string; why: string; source_file?: string | null; source_row?: string | null }
type Report = Plan & {
  id: string; sha256: string; dry_run: boolean; conflicts: ConflictRow[]; conflicts_total: number
  bundle: { built_at: string | null; version: number; files: FileRow[]; rules: Record<string, number | null>; rows: Record<string, number> }
}
type Run = {
  id: string; sha256: string; dry_run: boolean; label: string | null; at: string; actor_email: string | null; conflicts_kept: number
  counts: { plan?: Plan; bundle?: Record<string, number>; built_at?: string | null; conflicts_total?: number }
}
type Runs = { at: string; rows: Run[]; last_apply_at: string | null; max_mb: number; preview_hours: number }

const KEY = ['staff', 'imports']
const SECTIONS: [string, string][] = [['tasks', 'To-dos'], ['companies', 'Companies'], ['domains', 'Other domains'], ['people', 'People'], ['relationships', 'Pipeline rows'],
  ['pages', 'Pages'], ['touches', 'Touches'], ['sends', 'Send queue'], ['connections', 'Connections'], ['intro_routes', 'Intro routes']]
const LABEL = Object.fromEntries(SECTIONS)
// The 18 conflicts in INVENTORY.md, as the script numbers them.
const RULES: Record<string, string> = {
  1: 'Partner link slug has no page row', 2: 'Partner contact differs, people.csv wins', 3: 'Target person differs', 4: 'Page greets a different first name',
  5: 'Tier kept as separate fields', 6: 'Route normalized, raw kept', 7: 'Partner target not in the research list', 8: 'Connected, not proven by the export',
  9: 'Contacted per a list, not a send', 10: 'Tracker link-sent not trusted', 11: 'LinkedIn mark in two files, one touch', 12: 'F26 lists disagree, yc_pages wins',
  13: 'F26 company with a startup page', 14: 'Domain on more than one page row', 15: 'Startup art page missing from live-links', 16: 'Message matched no single company',
  17: 'README counts ignored', 18: 'Attribution, lane A',
}
const COMMAND = 'cd ~/partner-rewards && python3 scripts/crm_bundle.py --out ~/ernd-crm/bundle.json'

const sum = (p: Plan | undefined, k: keyof Sec): number | null =>
  p ? Object.values(p.sections).reduce((a, s) => a + (s[k] ?? 0), 0) : null
const short = (sha: string) => sha.slice(0, 12)
// The dev clock can sit ahead of the browser's, so a time may read as future.
const mb = (n: number) => `${(n / 1_048_576).toFixed(1)} MB`

export function ImportsPage() {
  const qc = useQueryClient()
  const kit = useKit()
  const input = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [report, setReport] = useState<Report | null>(null)
  const runs = useQuery({ queryKey: KEY, queryFn: () => api<Runs>('GET', '/api/staff/imports'), refetchInterval: 60_000 })
  const maxMb = runs.data?.max_mb ?? 25

  const preview = useMutation({
    mutationFn: (f: File) => api<Report>('POST', '/api/staff/imports/preview', f),
    onSuccess: (r) => { setReport(r); qc.invalidateQueries({ queryKey: KEY }) },
    onError: (e) => kit.toast(errorText(e), { bad: true }),
  })
  const apply = useMutation({
    mutationFn: ({ f, sha, id }: { f: File; sha: string; id: string }) => api<Report>('POST', `/api/staff/imports/apply?sha256=${encodeURIComponent(sha)}&preview=${encodeURIComponent(id)}`, f),
    onSuccess: (r) => {
      setReport(r)
      qc.invalidateQueries({ queryKey: ['staff'] })
      kit.toast(`Applied: ${num(sum(r, 'created'))} new, ${num(sum(r, 'changed'))} changed`)
    },
    onError: (e) => { qc.invalidateQueries({ queryKey: KEY }); kit.toast(errorText(e), { bad: true }) },
  })
  const busy = preview.isPending || apply.isPending

  const pick = (f: File | null) => {
    setReport(null)
    if (f && f.size > maxMb * 1_048_576) {
      kit.toast(`That file is ${mb(f.size)}. The limit is ${maxMb} MB.`, { bad: true })
      setFile(null)
      return
    }
    setFile(f)
  }

  const askApply = async () => {
    if (!file || !report || busy) return
    const s = report.sections
    const lines = SECTIONS.filter(([k]) => s[k] && (s[k].created || s[k].changed)).map(([k, l]) => `${l}: ${num(s[k].created)} new, ${num(s[k].changed)} changed`)
    const r = await kit.confirm({
      title: 'Apply this bundle',
      body: (
        <div className="st-stack st-imp-confirm" data-testid="imports-confirm-body">
          {lines.length ? <ul className="st-imp-list">{lines.map((l) => <li key={l}>{l}</li>)}</ul> : <p>Nothing to change. It records the run.</p>}
          <p>{num(report.stage_changes)} stage changes, recorded as import. {num(sum(report, 'kept'))} values a person set stay as they are.</p>
          <p>Touches are only added, never rewritten. File {short(report.sha256)}.</p>
        </div>
      ),
      confirm: 'Apply',
    })
    if (r.ok) apply.mutate({ f: file, sha: report.sha256, id: report.id })
  }

  const secCols: Column<[string, Sec]>[] = [
    { key: 'what', head: 'Section', primary: true, nowrap: true, cell: ([k]) => LABEL[k] ?? k },
    { key: 'created', head: 'New', num: true, cell: ([, s]) => num(s.created) },
    { key: 'changed', head: 'Changed', num: true, cell: ([, s]) => num(s.changed) },
    { key: 'matched', head: 'Already there', num: true, cell: ([, s]) => num(s.matched) },
    { key: 'kept', head: 'Kept as a person set it', num: true, cell: ([, s]) => num(s.kept) },
    { key: 'skipped', head: 'Skipped', num: true, cell: ([, s]) => num(s.skipped) },
    { key: 'conflicts', head: 'Conflicts', num: true, cell: ([, s]) => <span className={s.conflicts ? 'st-warn' : undefined}>{num(s.conflicts)}</span> },
  ]
  const fileCols: Column<FileRow>[] = [
    { key: 'file', head: 'File', primary: true, cell: (f) => <span className="st-mono st-imp-path">{f.file}</span> },
    { key: 'read', head: 'Read', num: true, cell: (f) => (f.missing ? <span className="st-warn">Missing</span> : num(f.read)) },
    { key: 'used', head: 'Used', num: true, cell: (f) => (f.missing ? '–' : num(f.used)) },
    { key: 'skipped', head: 'Skipped', num: true, cell: (f) => (f.missing ? '–' : num(f.skipped)) },
    { key: 'conflicts', head: 'Disagreed', num: true, cell: (f) => (f.missing ? '–' : num(f.conflicts)) },
    { key: 'error', head: 'Error', cell: (f) => (f.error ? <span className="st-bad">{f.error}</span> : null) },
  ]
  const conflictCols: Column<ConflictRow>[] = [
    { key: 'section', head: 'Section', cell: (c) => LABEL[c.section] ?? c.section },
    { key: 'key', head: 'Row', primary: true, cell: (c) => c.key },
    { key: 'why', head: 'Why', cell: (c) => c.why },
    { key: 'src', head: 'From', cell: (c) => (c.source_file ? `${c.source_file}${c.source_row ? `:${c.source_row}` : ''}` : null) },
  ]
  const runCols: Column<Run>[] = [
    { key: 'at', head: 'When', primary: true, nowrap: true, cell: (r) => <span title={when(r.at)}>{since(r.at)}</span> },
    { key: 'kind', head: 'Kind', cell: (r) => (r.dry_run ? 'Dry run' : <span className="st-strong">Applied</span>) },
    { key: 'who', head: 'Who', cell: (r) => r.actor_email },
    { key: 'created', head: 'New', num: true, cell: (r) => num(sum(r.counts.plan, 'created')) },
    { key: 'changed', head: 'Changed', num: true, cell: (r) => num(sum(r.counts.plan, 'changed')) },
    { key: 'kept', head: 'Kept', num: true, cell: (r) => num(sum(r.counts.plan, 'kept')) },
    { key: 'conflicts', head: 'Conflicts', num: true, cell: (r) => num(r.counts.conflicts_total ?? null) },
    { key: 'built', head: 'Bundle built', nowrap: true, cell: (r) => (r.counts.built_at ? <span title={when(r.counts.built_at)}>{since(r.counts.built_at)}</span> : '–') },
    { key: 'sha', head: 'File', cell: (r) => <span className="st-mono st-imp-path">{short(r.sha256)}</span> },
  ]

  const rules = report ? Object.entries(report.bundle.rules).sort((a, b) => Number(a[0]) - Number(b[0])) : []
  return (
    <Page title="Imports" testid="staff-imports">
      <Sources>
        {runs.data ? <Fresh at={runs.data.at} live label="History" limit={300} testid="imports-fresh" /> : null}
        {runs.data ? (runs.data.last_apply_at
          ? <Fresh at={runs.data.last_apply_at} label="Last apply" testid="imports-last-apply" />
          : <span className="st-fresh" data-testid="imports-last-apply">Never applied</span>) : null}
      </Sources>

      <Section title="Run this on your Mac" testid="imports-command">
        <div className="st-imp-cmd">
          <code data-testid="imports-command-text">{COMMAND}</code>
          <Copy text={COMMAND} testid="imports-command-copy" />
        </div>
        <p className="st-note">It reads ~/stitch/.claude and writes one file outside any git repo. No network, no database.</p>
      </Section>

      <Section title="Upload" testid="imports-upload">
        <div className="st-filters">
          <input ref={input} className="st-input st-imp-file" type="file" accept="application/json,.json" data-testid="imports-file"
            onChange={(e) => pick(e.target.files?.[0] ?? null)} />
          <Button tone="primary" disabled={!file || busy} onClick={() => file && preview.mutate(file)} data-testid="imports-preview">
            {preview.isPending ? 'Reading' : 'Dry run'}
          </Button>
          {file ? <span className="st-muted">{file.name}, {mb(file.size)}</span> : <span className="st-muted">Up to {maxMb} MB</span>}
        </div>
      </Section>

      {report ? (
        <Section
          title={report.dry_run ? 'Dry run' : 'Applied'}
          testid="imports-report"
          right={report.dry_run
            ? <Button tone="primary" disabled={busy || !file} onClick={askApply} data-testid="imports-apply">{apply.isPending ? 'Applying' : 'Apply'}</Button>
            : null}
        >
          <p className="st-note" data-testid="imports-report-sub">
            {report.dry_run ? 'Nothing written yet. ' : 'Written. '}
            File {short(report.sha256)}, bundle built {report.bundle.built_at ? since(report.bundle.built_at) : 'at an unknown time'}.
            {' '}{num(report.stage_changes)} stage changes.
          </p>
          <Table testid="imports-sections" rows={SECTIONS.filter(([k]) => report.sections[k]).map(([k]) => [k, report.sections[k]] as [string, Sec])}
            rowKey={([k]) => k} columns={secCols} flat />
        </Section>
      ) : null}

      {report && report.conflicts.length ? (
        <Section title="Conflicts with the records" count={num(report.conflicts_total)} testid="imports-conflicts">
          {report.conflicts_total > report.conflicts.length ? <p className="st-note">First {num(report.conflicts.length)} shown.</p> : null}
          <Table testid="imports-conflict-table" rows={report.conflicts} rowKey={(c) => `${c.section}:${c.key}:${c.why}`} columns={conflictCols} />
        </Section>
      ) : null}

      {report ? (
        <div className="st-two">
          <Section title="Files the script read" count={num(report.bundle.files.length)} testid="imports-files">
            <details className="st-imp-more" data-testid="imports-files-toggle">
              <summary>{num(report.bundle.files.filter((f) => f.missing).length)} missing, {num(report.bundle.files.filter((f) => f.error).length)} with errors. Show each file</summary>
              <Table testid="imports-file-table" rows={report.bundle.files} rowKey={(f) => f.file} columns={fileCols} empty="The bundle lists no files" flat />
            </details>
          </Section>
          <Section title="Rules the script applied" testid="imports-rules">
            {rules.length ? (
              <dl className="st-facts" data-testid="imports-rule-list">
                {rules.map(([k, n]) => <div key={k} className="st-imp-rule"><dt>{RULES[k] ?? `Rule ${k}`}</dt><dd className="st-num">{num(n)}</dd></div>)}
              </dl>
            ) : <p className="st-note">None reported</p>}
          </Section>
        </div>
      ) : null}

      <Section title="History" count={runs.data ? num(runs.data.rows.length) : undefined} testid="imports-history">
        {runs.isLoading ? <Busy /> : runs.error && !runs.data ? <Err error={runs.error} /> : (
          <Table testid="imports-history-table" rows={runs.data?.rows ?? []} rowKey={(r) => r.id} columns={runCols} empty="No imports yet" />
        )}
      </Section>
    </Page>
  )
}
